// src/api/request.ts
import { AxiosResponse } from 'axios';
import { createAxiosInstance } from './axiosInstance';

interface RequestConfig {
  method?: string;
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  base?: string;
  requiresAuth?: boolean;
  timeout?: number;
  contentType?: string;
  responseType?:
    | 'json'
    | 'text'
    | 'blob'
    | 'arraybuffer'
    | 'document'
    | 'stream';
}

export const request = <T = unknown>({
  method = 'GET',
  url,
  data,
  params,
  headers,
  base,
  requiresAuth = false,
  timeout,
  contentType = 'application/json',
  responseType = 'json',
  ...customConfig
}: RequestConfig): Promise<AxiosResponse<T>> => {
  const axiosInstance = createAxiosInstance(base, requiresAuth, timeout);

  return axiosInstance({
    method,
    url,
    data,
    params,
    headers: {
      ...(contentType && { 'Content-Type': contentType }),
      ...headers,
    },
    responseType,
    ...customConfig,
  });
};

// Legacy apiRequest export for backward compatibility
export const apiRequest = async <T = unknown>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const { method = 'GET', body, headers } = options;
  
  let data;
  if (body && typeof body === 'string') {
    try {
      data = JSON.parse(body);
    } catch {
      data = body;
    }
  } else {
    data = body;
  }
  
  const response = await request<T>({
    method: method as string,
    url,
    data,
    headers: headers as Record<string, string>,
    requiresAuth: true,
  });
  
  return response.data;
};
